export type UIHandle = {
  open(): void;
  close(): void;
};

export type UISubmitContext = {
  /** True if the user kept the "Attach screenshot" checkbox enabled. */
  includeScreenshot: boolean;
};

export type UIOptions = {
  onSubmit(
    message: string,
    type: "bug" | "feature" | "other",
    ctx: UISubmitContext,
  ): Promise<void>;
  /**
   * Show the "Attach screenshot" toggle. The host decides this from the
   * server-side config so users never see an option that won't actually work
   * (e.g. when S3 isn't configured on the deployment).
   */
  screenshotsAvailable: boolean;
};

const STYLES = `
  :host { all: initial; }
  .btn {
    position: fixed; bottom: 20px; right: 20px; z-index: 2147483646;
    width: 48px; height: 48px; border-radius: 9999px; border: 0;
    background: #111; color: white; cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,0.2);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    display: flex; align-items: center; justify-content: center;
  }
  .btn:hover { background: #222; }
  .overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 2147483647;
    display: flex; align-items: flex-end; justify-content: flex-end; padding: 20px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .modal {
    background: #fff; border-radius: 12px; width: 360px; max-width: 100%;
    padding: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.2);
    color: #111;
  }
  .modal h3 { margin: 0 0 8px; font-size: 16px; }
  .row { display: flex; gap: 6px; margin: 8px 0; }
  .chip {
    padding: 6px 10px; border-radius: 9999px; border: 1px solid #e5e7eb;
    background: #fff; cursor: pointer; font-size: 12px;
  }
  .chip[aria-pressed="true"] { background: #111; color: #fff; border-color: #111; }
  textarea {
    width: 100%; min-height: 100px; padding: 10px; border: 1px solid #e5e7eb;
    border-radius: 8px; font-family: inherit; font-size: 14px; box-sizing: border-box;
    resize: vertical;
  }
  .actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
  button.primary, button.secondary {
    padding: 8px 14px; border-radius: 8px; font-size: 13px; cursor: pointer;
    font-family: inherit;
  }
  button.primary { background: #111; color: #fff; border: 0; }
  button.primary:disabled { opacity: 0.5; cursor: not-allowed; }
  button.secondary { background: #fff; border: 1px solid #e5e7eb; }
  .status { font-size: 12px; color: #6b7280; margin-top: 6px; }
  .opts {
    display: flex; align-items: center; gap: 6px; margin-top: 8px;
    font-size: 12px; color: #4b5563;
  }
  .opts input { accent-color: #111; cursor: pointer; }
  .opts label { cursor: pointer; user-select: none; }
  .hint { font-size: 11px; color: #9ca3af; margin-left: auto; }
  input.hp { display: none; }
`;

export function mountUI(opts: UIOptions): UIHandle {
  const host = document.createElement("div");
  host.setAttribute("data-feedbase-root", "");
  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = STYLES;
  shadow.appendChild(style);

  const btn = document.createElement("button");
  btn.className = "btn";
  btn.setAttribute("aria-label", "Send feedback");
  btn.innerHTML = "<span>💬</span>";
  shadow.appendChild(btn);

  let overlay: HTMLDivElement | null = null;

  function close() {
    overlay?.remove();
    overlay = null;
  }

  function open() {
    if (overlay) return;
    overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close();
    });

    const modal = document.createElement("div");
    modal.className = "modal";
    modal.innerHTML = `
      <h3>Send feedback</h3>
      <div class="row" role="tablist">
        <button class="chip" data-type="bug" aria-pressed="true">🐛 Bug</button>
        <button class="chip" data-type="feature" aria-pressed="false">✨ Feature</button>
        <button class="chip" data-type="other" aria-pressed="false">💭 Other</button>
      </div>
      <textarea placeholder="What happened? Steps to reproduce help a lot." maxlength="4000"></textarea>
      <input class="hp" name="website" tabindex="-1" autocomplete="off" />
      ${
        opts.screenshotsAvailable
          ? `<div class="opts">
               <input type="checkbox" id="fb-screenshot" checked />
               <label for="fb-screenshot">📷 Attach screenshot of this page</label>
               <span class="hint">⌘↵ to send</span>
             </div>`
          : `<div class="opts"><span class="hint" style="margin-left:0">⌘↵ to send</span></div>`
      }
      <div class="actions">
        <button class="secondary" data-action="cancel">Cancel</button>
        <button class="primary" data-action="submit" disabled>Send</button>
      </div>
      <div class="status" aria-live="polite"></div>
    `;
    overlay.appendChild(modal);
    shadow.appendChild(overlay);

    let type: "bug" | "feature" | "other" = "bug";
    const chips = modal.querySelectorAll<HTMLButtonElement>(".chip");
    chips.forEach((c) =>
      c.addEventListener("click", () => {
        type = c.dataset.type as typeof type;
        chips.forEach((x) => x.setAttribute("aria-pressed", x === c ? "true" : "false"));
      }),
    );

    const ta = modal.querySelector("textarea")!;
    const submit = modal.querySelector<HTMLButtonElement>('[data-action="submit"]')!;
    const cancel = modal.querySelector<HTMLButtonElement>('[data-action="cancel"]')!;
    const status = modal.querySelector<HTMLDivElement>(".status")!;
    const hp = modal.querySelector<HTMLInputElement>(".hp")!;
    const screenshotCheckbox = modal.querySelector<HTMLInputElement>("#fb-screenshot");

    ta.addEventListener("input", () => {
      submit.disabled = ta.value.trim().length < 3;
    });
    cancel.addEventListener("click", close);

    let isSubmitting = false;
    async function doSubmit() {
      if (isSubmitting || submit.disabled) return;
      if (hp.value) {
        // bot
        close();
        return;
      }
      isSubmitting = true;
      submit.disabled = true;
      status.textContent = "Sending…";
      const includeScreenshot = screenshotCheckbox?.checked ?? false;
      try {
        await opts.onSubmit(ta.value.trim(), type, { includeScreenshot });
        status.textContent = "Thanks! We got it.";
        setTimeout(close, 1000);
      } catch (err) {
        status.textContent =
          err instanceof Error ? `Failed: ${err.message}` : "Failed to send";
        submit.disabled = false;
        isSubmitting = false;
      }
    }
    submit.addEventListener("click", doSubmit);

    // Keyboard shortcuts inside the modal:
    //   ⌘↵ / ⌃↵  — submit (matches Slack, Linear, GitHub comments)
    //   Esc      — close
    overlay.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void doSubmit();
      }
    });

    setTimeout(() => ta.focus(), 50);
  }

  btn.addEventListener("click", open);

  const attach = () => {
    if (document.body) document.body.appendChild(host);
    else document.addEventListener("DOMContentLoaded", () => document.body.appendChild(host));
  };
  attach();

  return { open, close };
}
